import {
    PERSIST_REPORT_FILTERS_STORAGE_KEY,
    UPCOMING_DUE_DATE_FILTERS_STORAGE_KEY,
} from '../../../modules/cap/TicketList/constants/ticketlist.constants';
import { CHART_TYPE } from '../../../constants/common/chart/chartConst';
import { logError } from '../../../utils/tools/ErrorLoggers';

const VALID_CHART_TYPES = Object.values(CHART_TYPE);

export type DrillDownFilter = {
    selectedId: number;
    selectedWorkGropeName: string;
    selectedChart: string;
    selectedWorkgroup: string | number | null;
    type: string;
};

export type UpcomingDueDatePersistedFilters = {
    selectedReport?: string;
    segmentBy?: string;
    showBy?: string;
    isTagFilterApplied?: boolean;
    selectedDate?: string | null;
    workGroupFilter?: string | number | null;
    isWorkGroupFilterApplied?: boolean;
    legendFilter?: string | null;
    selectedChart?: string;
    selectedId?: number | null;
    isDrillDownActive?: boolean;
    isDrillDownAllowed?: boolean;
    selectedWorkGropeName?: string;
    drillDownData?: DrillDownFilter[];
};

export const isValidDrillDownData = (drillDownData: unknown): drillDownData is DrillDownFilter[] => {
    if (!Array.isArray(drillDownData)) {
        return false;
    }

    return drillDownData.every(
        drillDownItem =>
            drillDownItem &&
            typeof drillDownItem === 'object' &&
            typeof drillDownItem.selectedId === 'number' &&
            typeof drillDownItem.selectedWorkGropeName === 'string' &&
            typeof drillDownItem.selectedChart === 'string' &&
            (drillDownItem.selectedWorkgroup === null ||
                typeof drillDownItem.selectedWorkgroup === 'string' ||
                typeof drillDownItem.selectedWorkgroup === 'number') &&
            typeof drillDownItem.type === 'string',
    );
};

export const getStoredUpcomingDueDateReportFilters = (): UpcomingDueDatePersistedFilters | null => {
    try {
        const persistedFilters = sessionStorage.getItem(UPCOMING_DUE_DATE_FILTERS_STORAGE_KEY);
        const persistedFiltersFlag = sessionStorage.getItem(PERSIST_REPORT_FILTERS_STORAGE_KEY);
        if (!persistedFilters || persistedFiltersFlag !== 'true') {
            return null;
        }
        let parsedFilters: UpcomingDueDatePersistedFilters;
        try {
            parsedFilters = JSON.parse(persistedFilters) as UpcomingDueDatePersistedFilters;
        } catch (parseError) {
            logError('Failed to parse persisted report filters', { parseError });
            sessionStorage.removeItem(UPCOMING_DUE_DATE_FILTERS_STORAGE_KEY);
            sessionStorage.removeItem(PERSIST_REPORT_FILTERS_STORAGE_KEY);
            return null;
        }
        const isValidSelectedDate =
            parsedFilters.selectedDate === undefined ||
            parsedFilters.selectedDate === null ||
            (typeof parsedFilters.selectedDate === 'string' &&
                !Number.isNaN(new Date(parsedFilters.selectedDate).getTime()));
        const isValidReportName =
            parsedFilters.selectedReport === undefined || typeof parsedFilters.selectedReport === 'string';
        const isValidSegmentBy = parsedFilters.segmentBy === undefined || typeof parsedFilters.segmentBy === 'string';
        const isValidShowBy = parsedFilters.showBy === undefined || typeof parsedFilters.showBy === 'string';
        const isValidTagFilter =
            parsedFilters.isTagFilterApplied === undefined || typeof parsedFilters.isTagFilterApplied === 'boolean';
        const isValidWorkGroupFilter =
            parsedFilters.workGroupFilter === undefined ||
            parsedFilters.workGroupFilter === null ||
            typeof parsedFilters.workGroupFilter === 'string' ||
            typeof parsedFilters.workGroupFilter === 'number';
        const isValidLegendFilter =
            parsedFilters.legendFilter === undefined ||
            parsedFilters.legendFilter === null ||
            typeof parsedFilters.legendFilter === 'string';
        const isValidSelectedChart =
            parsedFilters.selectedChart === undefined ||
            (typeof parsedFilters.selectedChart === 'string' && VALID_CHART_TYPES.includes(parsedFilters.selectedChart));
        const isValidSelectedId =
            parsedFilters.selectedId === undefined ||
            parsedFilters.selectedId === null ||
            typeof parsedFilters.selectedId === 'number';
        const isValidDrillDownActive =
            parsedFilters.isDrillDownActive === undefined || typeof parsedFilters.isDrillDownActive === 'boolean';
        const isValidDrillDownAllowed =
            parsedFilters.isDrillDownAllowed === undefined || typeof parsedFilters.isDrillDownAllowed === 'boolean';
        const isValidSelectedWorkGroupName =
            parsedFilters.selectedWorkGropeName === undefined || typeof parsedFilters.selectedWorkGropeName === 'string';
        const isValidWorkGroupFilterApplied =
            parsedFilters.isWorkGroupFilterApplied === undefined ||
            typeof parsedFilters.isWorkGroupFilterApplied === 'boolean';

        if (
            !isValidSelectedDate ||
            !isValidReportName ||
            !isValidSegmentBy ||
            !isValidShowBy ||
            !isValidTagFilter ||
            !isValidWorkGroupFilter ||
            !isValidLegendFilter ||
            !isValidSelectedChart ||
            !isValidSelectedId ||
            !isValidDrillDownActive ||
            !isValidDrillDownAllowed ||
            !isValidSelectedWorkGroupName ||
            !isValidWorkGroupFilterApplied
        ) {
            return null;
        }
        if (parsedFilters.drillDownData !== undefined && !isValidDrillDownData(parsedFilters.drillDownData)) {
            return null;
        }
        return parsedFilters;
    } catch (storageError) {
        logError('Failed to access session storage for report filters', { storageError });
        return null;
    }
};
